import React from 'react';
import Icon from '../AppIcon';

const OnboardingProgress = ({ 
  currentStep = 1, 
  totalSteps = 4,
  steps = [],
  onStepClick = () => {}
}) => {
  const defaultSteps = [
    { id: 1, label: 'Account Setup', icon: 'User' },
    { id: 2, label: 'Business Details', icon: 'Briefcase' },
    { id: 3, label: 'Goals & Objectives', icon: 'Target' },
    { id: 4, label: 'Preferences', icon: 'Settings' }
  ];

  const stepList = steps?.length > 0 ? steps : defaultSteps?.slice(0, totalSteps);

  const getStepStatus = (stepId) => {
    if (stepId < currentStep) return 'completed';
    if (stepId === currentStep) return 'active';
    return 'upcoming';
  };

  return (
    <div className="w-full">
      <div className="hidden md:flex items-center justify-between mb-8">
        {stepList?.map((step, index) => {
          const status = getStepStatus(step?.id);
          const isClickable = step?.id <= currentStep;

          return (
            <React.Fragment key={step?.id}>
              <button
                onClick={() => isClickable && onStepClick(step?.id)}
                disabled={!isClickable}
                className={`flex flex-col items-center gap-3 transition-smooth ${
                  isClickable ? 'cursor-pointer' : 'cursor-not-allowed'
                }`}
              >
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-smooth ${
                    status === 'completed'
                      ? 'bg-success text-success-foreground elevation-sm'
                      : status === 'active' ?'bg-primary text-primary-foreground elevation-md' :'bg-muted text-muted-foreground'
                  }`}
                >
                  {status === 'completed' ? (
                    <Icon name="Check" size={20} color="var(--color-success-foreground)" strokeWidth={2.5} />
                  ) : (
                    <Icon 
                      name={step?.icon} 
                      size={20} 
                      color={status === 'active' ? 'var(--color-primary-foreground)' : 'currentColor'} 
                    />
                  )}
                </div>
                <div className="text-center">
                  <p
                    className={`text-sm font-medium font-body transition-smooth ${
                      status === 'active' ? 'text-foreground' : 'text-muted-foreground'
                    }`}
                  >
                    {step?.label}
                  </p>
                  <p className="text-xs text-muted-foreground font-caption mt-1">
                    Step {step?.id} of {totalSteps}
                  </p>
                </div>
              </button>
              {index < stepList?.length - 1 && (
                <div className="flex-1 h-0.5 mx-4 relative">
                  <div className="absolute inset-0 bg-muted" />
                  <div
                    className={`absolute inset-0 bg-primary transition-smooth ${
                      step?.id < currentStep ? 'w-full' : 'w-0'
                    }`}
                    style={{ transitionDuration: '500ms' }}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
      <div className="md:hidden">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center bg-primary text-primary-foreground elevation-md`}
            >
              <Icon 
                name={stepList?.[currentStep - 1]?.icon || 'Circle'} 
                size={18} 
                color="var(--color-primary-foreground)" 
              />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground font-body">
                {stepList?.[currentStep - 1]?.label || `Step ${currentStep}`}
              </p>
              <p className="text-xs text-muted-foreground font-caption">
                Step {currentStep} of {totalSteps}
              </p>
            </div>
          </div>
        </div>

        <div className="relative h-2 bg-muted rounded-full overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 bg-primary transition-smooth rounded-full"
            style={{ 
              width: `${(currentStep / totalSteps) * 100}%`,
              transitionDuration: '500ms'
            }}
          />
        </div>

        <div className="mt-4 flex justify-between">
          {stepList?.map((step) => {
            const status = getStepStatus(step?.id);
            return (
              <button
                key={step?.id}
                onClick={() => step?.id <= currentStep && onStepClick(step?.id)}
                disabled={step?.id > currentStep}
                className={`w-8 h-8 rounded-full flex items-center justify-center transition-smooth ${
                  status === 'completed'
                    ? 'bg-success text-success-foreground'
                    : status === 'active' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground'
                } ${step?.id <= currentStep ? 'cursor-pointer' : 'cursor-not-allowed'}`}
              >
                {status === 'completed' ? (
                  <Icon name="Check" size={14} color="var(--color-success-foreground)" strokeWidth={2.5} />
                ) : (
                  <span className="text-xs font-medium font-caption">{step?.id}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default OnboardingProgress;